import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FingerprintSimple, CalendarBlank, Megaphone } from "@phosphor-icons/react";

export default function EmpHome() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/dashboard/employee").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const punch = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/attendance/${kind}`);
      toast.success(kind === "check-in" ? "Checked in. Have a great shift!" : "Checked out. See you tomorrow!");
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!d) return <div className="text-stone-400">Loading…</div>;

  const today = d.today;
  const checkedIn = today?.check_in && !today?.check_out;
  const done = today?.check_in && today?.check_out;

  const punchStatusLabel = () => {
    if (done) return `${today.check_in} → ${today.check_out}`;
    if (checkedIn) return `In at ${today.check_in}`;
    return "Not checked in";
  };
  const punchButtonLabel = () => {
    if (busy) return "…";
    return checkedIn ? "Check out" : "Check in";
  };

  return (
    <div className="space-y-5" data-testid="employee-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Hi, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-stone-500">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      <div className="bg-secondary text-white rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-300">Today</div>
            <div className="font-heading text-2xl font-extrabold mt-1 tabular-nums" data-testid="today-punch-status">
              {punchStatusLabel()}
            </div>
            {done && <div className="text-sm text-stone-300 mt-1">{today.hours} hrs worked</div>}
          </div>
          <FingerprintSimple size={44} weight="duotone" className="text-[#F09A5F]" />
        </div>
        {!done && (
          <button onClick={() => punch(checkedIn ? "check-out" : "check-in")} disabled={busy}
            data-testid={checkedIn ? "check-out-button" : "check-in-button"}
            className="tap-scale mt-5 w-full min-h-[52px] rounded-full bg-primary text-white font-bold text-lg hover:bg-[#A63F07] transition-colors duration-200 disabled:opacity-60">
            {punchButtonLabel()}
          </button>
        )}
        {done && <div className="mt-4 text-center text-sm font-semibold text-green-300" data-testid="day-complete-label">Day complete ✓</div>}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {d.leave_balances.map((b) => (
          <div key={b.leave_type} className="bg-white border border-stone-200 rounded-2xl p-4 text-center" data-testid={`balance-${b.leave_type}`}>
            <div className="font-heading text-2xl font-extrabold tabular-nums">{b.balance}</div>
            <div className="text-[11px] text-stone-500 font-semibold">{b.leave_type} left</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><CalendarBlank size={18} weight="duotone" className="text-primary" /><h3 className="font-heading font-bold text-sm">Upcoming holidays</h3></div>
        {d.upcoming_holidays.length === 0 ? <p className="text-sm text-stone-400">No upcoming holidays.</p> : (
          <div className="space-y-2">
            {d.upcoming_holidays.map((h) => (
              <div key={h.date} className="flex justify-between text-sm">
                <span className="font-semibold">{h.name}</span><span className="text-stone-500 tabular-nums">{h.date}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Megaphone size={18} weight="duotone" className="text-primary" /><h3 className="font-heading font-bold text-sm">Announcements</h3></div>
        <div className="space-y-4">
          {d.announcements.map((a) => (
            <div key={a.id}>
              <div className="font-semibold text-sm">{a.title}</div>
              <p className="text-xs text-stone-500 mt-0.5">{a.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
